'use strict';

/**
 * Generador de OTS (Open Team Sheet) multilenguaje en una sola pagina A4.
 *
 * Colocar este archivo en la misma carpeta que el generador PDF actual.
 * Reutiliza las fuentes, los diccionarios, los traductores y Koffing
 * presentes en el proyecto. No modifica los modos "open" y "staff".
 *
 * Uso desde generateTeamPDF(data):
 *   if (
 *     data.sheet === 'allLanguage' ||
 *     data.lang === 'allLanguage' ||
 *     data.allLanguage === true
 *   ) {
 *     return generateAllLanguageOTS(data);
 *   }
 */

import { jsPDF } from 'jspdf';
import { Koffing } from './koff.mjs';

import fontLatin from './fonts/fontLatin.js';

import PokeTranslator from './resources/Pokes/TranslatorPokes.js';
import AbilityTranslator from './resources/Abilities/TranslatorAbilities.js';
import ItemTranslator from './resources/Items/TranslatorItems.js';
import MoveTranslator from './resources/Moves/TranslatorMoves.js';
import NaturesTranslator from './resources/Natures/TranslatorNatures.js';

/**
 * Orden y encabezados equivalentes a la OTS multilenguaje de referencia.
 * Codigos reales de la plataforma: En, Es, Fre, Ger, Ita y EsLa.
 */
const OTS_LANGUAGES = [
  {
    lang: 'En', short: 'EN', title: 'English', font: 'otsLatin',
    labels: ['Pokémon', 'Nature', 'Ability', 'Held Item', 'Move 1', 'Move 2', 'Move 3', 'Move 4']
  },
  {
    lang: 'Es', short: 'ES', title: 'Spanish', font: 'otsLatin',
    labels: ['Pokémon', 'Naturaleza', 'Habilidad', 'Objeto equipado', 'Movimiento 1', 'Movimiento 2', 'Movimiento 3', 'Movimiento 4']
  },
  {
    lang: 'EsLa', short: 'ES-LA', title: 'Spanish Latam', font: 'otsLatin',
    labels: ['Pokémon', 'Naturaleza', 'Habilidad', 'Objeto equipado', 'Movimiento 1', 'Movimiento 2', 'Movimiento 3', 'Movimiento 4']
  },
  {
    lang: 'Fre', short: 'FR', title: 'French', font: 'otsLatin',
    labels: ['Pokémon', 'Nature', 'Talent', 'Objet tenu', 'Capacité 1', 'Capacité 2', 'Capacité 3', 'Capacité 4']
  },
  {
    lang: 'Ger', short: 'DE', title: 'German', font: 'otsLatin',
    labels: ['Pokémon', 'Wesen', 'Fähigkeit', 'Getragenes Item', 'Attacke 1', 'Attacke 2', 'Attacke 3', 'Attacke 4']
  },
  {
    lang: 'Ita', short: 'IT', title: 'Italian', font: 'otsLatin',
    labels: ['Pokémon', 'Natura', 'Abilità', 'Strumento tenuto', 'Mossa 1', 'Mossa 2', 'Mossa 3', 'Mossa 4']
  }
];

const OTS_LAYOUT = {
  pageWidth: 210,      // A4 vertical, milimetros
  x: 8,
  top: 18.5,
  totalWidth: 194,
  labelWidth: 29.5,
  rowHeight: 5.45,      // Seis bloques de 8 filas: 261.6 mm, en una sola pagina
  rowsPerLanguage: 8,
  pokemonColumns: 6
};

const NATURE_ES_TO_EN = {
  fuerte: 'Hardy',
  hurana: 'Lonely',
  audaz: 'Brave',
  firme: 'Adamant',
  picara: 'Naughty',
  osada: 'Bold',
  docil: 'Docile',
  placida: 'Relaxed',
  agitada: 'Impish',
  floja: 'Lax',
  miedosa: 'Timid',
  activa: 'Hasty',
  seria: 'Serious',
  alegre: 'Jolly',
  ingenua: 'Naive',
  modesta: 'Modest',
  afable: 'Mild',
  mansa: 'Quiet',
  timida: 'Bashful',
  alocada: 'Rash',
  serena: 'Calm',
  amable: 'Gentle',
  grosera: 'Sassy',
  cauta: 'Careful',
  rara: 'Quirky'
};

function cleanText(value) {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

function englishNature(value) {
  const raw = cleanText(value);
  const normalized = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  return NATURE_ES_TO_EN[normalized] || raw;
}

/**
 * Traduce una entrada a partir de la clave comun proporcionada por
 * TranslatorPokes/Abilities/Items/Moves/Natures.
 * Si falta una entrada individual, muestra el texto original en vez de
 * escribir "undefined" o dejar la celda vacia inesperadamente.
 */
function translateValue(value, translator, dictionary, alternateValue) {
  const original = cleanText(value);
  if (!original) return '';

  const alternative = cleanText(alternateValue);
  const translatedKey =
    translator?.[original] ??
    (alternative ? translator?.[alternative] : undefined);

  if (translatedKey === undefined || translatedKey === null) {
    return original;
  }

  const translated = dictionary?.[translatedKey];
  return typeof translated === 'string' && translated.trim()
    ? translated.trim()
    : original;
}

async function loadLanguageTranslations(lang) {
  // No se cargan Types porque la OTS de referencia no muestra tipos.
  // Se reutiliza el mismo patron de importaciones del generador existente.
  try {
    const [pokes, natures, abilities, items, moves] = await Promise.all([
      import(`./resources/Pokes/Pokes${lang}.js`),
      import(`./resources/Natures/Natures${lang}.js`),
      import(`./resources/Abilities/Abilities${lang}.js`),
      import(`./resources/Items/Items${lang}.js`),
      import(`./resources/Moves/Moves${lang}.js`)
    ]);

    return {
      pokes: pokes.default,
      natures: natures.default,
      abilities: abilities.default,
      items: items.default,
      moves: moves.default
    };
  } catch (error) {
    throw new Error(
      `No se pudieron cargar las traducciones del idioma ${lang}. ` +
      `Revisa los archivos Pokes${lang}, Natures${lang}, ` +
      `Abilities${lang}, Items${lang} y Moves${lang}. ` +
      `Detalle: ${error.message}`
    );
  }
}

/**
 * Confecciona las ocho filas de cada columna Pokemon.
 */
function translatePokemon(pokemon, dictionaries) {
  if (!pokemon) return Array(8).fill('');

  const originalNature = cleanText(pokemon.nature) || 'Serious';
  const englishName = englishNature(originalNature);

  const moves = Array.isArray(pokemon.moves) ? pokemon.moves : [];

  return [
    translateValue(pokemon.name, PokeTranslator, dictionaries.pokes),
    translateValue(originalNature, NaturesTranslator, dictionaries.natures, englishName),
    translateValue(pokemon.ability, AbilityTranslator, dictionaries.abilities),
    pokemon.item
      ? translateValue(pokemon.item, ItemTranslator, dictionaries.items)
      : '-',
    ...Array.from({ length: 4 }, (_, index) => (
      translateValue(moves[index], MoveTranslator, dictionaries.moves)
    ))
  ];
}

function installFonts(doc) {
  // Los seis idiomas utilizan caracteres del alfabeto latino.
  // No hace falta cargar fuentes japonesas, coreanas ni chinas.
  doc.addFileToVFS('otsLatin.ttf', fontLatin);
  doc.addFont('otsLatin.ttf', 'otsLatin', 'normal');
}

/**
 * Evita que nombres de formas largas o traducciones extensas
 * se superpongan a las columnas vecinas.
 */
function drawFittedText(doc, value, x, y, maxWidth, options = {}) {
  let text = cleanText(value);
  if (!text) return;

  const baseSize = options.size ?? 7.6;
  const minSize = options.minSize ?? 4.0;
  let fontSize = baseSize;

  doc.setFontSize(fontSize);

  while (
    fontSize > minSize &&
    doc.getTextWidth(text) > maxWidth
  ) {
    fontSize = Math.max(minSize, Math.round((fontSize - 0.2) * 10) / 10);
    doc.setFontSize(fontSize);
  }

  // Si ni siquiera el tamano minimo alcanza, truncar solamente esa celda.
  if (doc.getTextWidth(text) > maxWidth) {
    const chars = Array.from(text);
    while (chars.length > 0 && doc.getTextWidth(`${chars.join('')}...`) > maxWidth) {
      chars.pop();
    }
    text = chars.length ? `${chars.join('')}...` : '';
  }

  if (text) {
    doc.text(text, x, y, { align: options.align || 'center' });
  }
}

function drawLanguageTable(doc, language, dictionaries, pokemon) {
  const { x, top, totalWidth, labelWidth, rowHeight, rowsPerLanguage } = OTS_LAYOUT;
  const pokemonWidth = (totalWidth - labelWidth) / OTS_LAYOUT.pokemonColumns;
  const index = OTS_LANGUAGES.findIndex(entry => entry.lang === language.lang);
  const blockTop = top + index * rowsPerLanguage * rowHeight;
  const blockHeight = rowsPerLanguage * rowHeight;

  // Fondo gris de la primera fila, igual al documento de referencia.
  doc.setFillColor(225, 225, 225);
  doc.rect(x, blockTop, totalWidth, rowHeight, 'F');

  doc.setDrawColor(30, 30, 30);
  doc.setLineWidth(0.18);

  // Bordes horizontales.
  for (let r = 0; r <= rowsPerLanguage; r++) {
    const rowY = blockTop + r * rowHeight;
    doc.line(x, rowY, x + totalWidth, rowY);
  }

  // Lineas verticales, incluyendo el borde externo.
  doc.line(x, blockTop, x, blockTop + blockHeight);
  doc.line(x + labelWidth, blockTop, x + labelWidth, blockTop + blockHeight);

  for (let c = 1; c <= OTS_LAYOUT.pokemonColumns; c++) {
    const colX = x + labelWidth + c * pokemonWidth;
    doc.line(colX, blockTop, colX, blockTop + blockHeight);
  }

  const translatedPokemon = pokemon.map(entry => translatePokemon(entry, dictionaries));

  // Columna de rotulos de la izquierda y abreviatura del idioma.
  for (let row = 0; row < rowsPerLanguage; row++) {
    const baseline = blockTop + row * rowHeight + rowHeight * 0.74;
    doc.setFont(language.font, 'normal');
    doc.setTextColor(12, 12, 12);

    if (row === 0) {
      doc.setFont('otsLatin', 'normal');
      drawFittedText(doc, language.short, x + 0.65, baseline, 9.0, {
        align: 'left', size: 8.6, minSize: 6.2
      });
      doc.setFont(language.font, 'normal');
      drawFittedText(doc, language.labels[0], x + 19.4, baseline, 18.7, {
        size: 9.0, minSize: 6.1
      });
    } else {
      drawFittedText(doc, language.labels[row], x + labelWidth / 2, baseline, labelWidth - 1.5, {
        size: 9.0, minSize: 5.5
      });
    }
  }

  // Seis columnas Pokemon y sus datos para este idioma.
  for (let col = 0; col < OTS_LAYOUT.pokemonColumns; col++) {
    const centerX = x + labelWidth + (col + 0.5) * pokemonWidth;

    for (let row = 0; row < rowsPerLanguage; row++) {
      const baseline = blockTop + row * rowHeight + rowHeight * 0.74;
      const text = translatedPokemon[col]?.[row] || '';
      if (!text) continue;

      doc.setFont(language.font, 'normal');
      doc.setTextColor(10, 10, 10);
      drawFittedText(doc, text, centerX, baseline, pokemonWidth - 1.0, {
        size: row === 0 ? 8.7 : 9.0,
        minSize: 4.2
      });
    }
  }
}

function ageDivisionText(ageDivision) {
  const divisions = ['Junior', 'Senior', 'Master'];
  const number = Number(ageDivision);

  if (ageDivision !== '' && ageDivision !== null && ageDivision !== undefined &&
      Number.isInteger(number) && number >= 0 && number <= 2) {
    return divisions[number];
  }

  return cleanText(ageDivision);
}

function safeFilePart(value) {
  const safeText = cleanText(value)
    .replace(/[\\/:*?"<>|]/g, '-');

  return Array.from(safeText)
    .map(char => char.codePointAt(0) < 32 ? '-' : char)
    .join('')
    .slice(0, 90);
}

/**
 * Genera una sola pagina OTS con los seis idiomas disponibles en la plataforma.
 *
 * @param {Object} data - Los mismos datos de generateTeamPDF.
 * @param {string} data.paste - Equipo en formato Pokemon Showdown.
 * @param {string} [data.playerName] - Nombre del jugador.
 * @param {string} [data.teamName] - Nombre del equipo.
 * @param {number|string} [data.ageDivision] - 0 Junior, 1 Senior, 2 Master.
 * @param {string|number} [data.playerId] - Identificador para el archivo.
 * @returns {Promise<jsPDF>} Documento generado, guardado mediante doc.save().
 */
export async function generateAllLanguageOTS(data = {}) {
  const { paste, playerName = '', teamName = '', ageDivision, playerId = '' } = data;

  if (typeof paste !== 'string' || !paste.trim()) {
    throw new Error('allLanguage requiere un equipo en formato Showdown (data.paste).');
  }

  let parsedTeam;
  try {
    parsedTeam = Koffing.parse(paste);
  } catch (error) {
    throw new Error(`No se pudo interpretar el paste de Showdown: ${error.message}`);
  }

  const pokemon = parsedTeam?.teams?.[0]?.pokemon;
  if (!Array.isArray(pokemon) || pokemon.length === 0) {
    throw new Error('El paste no contiene un equipo Pokemon valido.');
  }
  if (pokemon.length > 6) {
    throw new Error('Una OTS admite un maximo de 6 Pokemon.');
  }

  // Descarga en paralelo los cinco diccionarios de los seis idiomas.
  const translations = await Promise.all(
    OTS_LANGUAGES.map(language => loadLanguageTranslations(language.lang))
  );

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  installFonts(doc);

  // Encabezado similar a la hoja multilenguaje de referencia.
  const playerTitle = [cleanText(playerName), cleanText(teamName)]
    .filter(Boolean)
    .join(' - ') || 'Pokémon Team';

  doc.setFont('otsLatin', 'normal');
  doc.setTextColor(0, 0, 0);
  drawFittedText(doc, playerTitle, 20, 11, 154, {
    align: 'left', size: 14, minSize: 8
  });

  drawFittedText(doc, ageDivisionText(ageDivision), 200, 11, 30, {
    align: 'right', size: 13, minSize: 8
  });

  // Seis bloques en una sola hoja, con las mismas seis columnas.
  for (let index = 0; index < OTS_LANGUAGES.length; index++) {
    drawLanguageTable(doc, OTS_LANGUAGES[index], translations[index], pokemon);
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.setTextColor(145, 145, 145);
  doc.text('Generated by pokecircuit.com', 201, 294, { align: 'right' });

  const fileName = [safeFilePart(playerId), safeFilePart(playerName), 'allLanguage-OTS']
    .filter(Boolean)
    .join('-') + '.pdf';

  doc.save(fileName);
  return doc;
}

export default generateAllLanguageOTS;
